import { Theme, css } from '@emotion/react';
import React from 'react';

interface SecondaryHeaderProps {
  variant: 'primary' | 'warning';
  /**
   * Alignment of the secondary header content. Note that this ignored at mobile widths,
   * the content in that case is always centered.
   */
  align: 'left' | 'center' | 'right';
}

/** The intention here is to display secondary headers at the top of the page, and
 * as the page content scrolls the header will stick in place above (and at a
 * higher z-index) than the secondary headers.
 *
 * There seemed to be previous attempts in this scss using relative positioning (??), but
 * other than setting an appropriate z-index styling doesn't have much to offer here
 * Not in a way that will play nicely with sticky positioning, certainly!
 *
 * To get what we want, we just need to be sure that any secondary header content
 * is placed in the DOM in an appropriate place, such that it will naturally render at
 * the top of the screen. Our styles and DOM positioning for the header will simply take
 * care of the rest.
 */
const computeStyles = (
  { breakpoints, colors }: Theme,
  { align, variant }: SecondaryHeaderProps
) => {
  const desktopJustifyContent =
    align === 'left' ? 'flex-start' : align === 'center' ? 'center' : 'flex-end';
  const backgroundColor = colors[variant === 'primary' ? 'bgDarker' : 'bgWarningBase'];

  return css`
    align-items: center;
    background: ${backgroundColor};
    color: ${colors.fgContrast};
    display: flex;
    height: 3.6rem;
    justify-content: center;
    padding: 1rem 1.2rem;
    width: 100%;
    z-index: 3;

    ${breakpoints.up('sm')} {
      justify-content: ${desktopJustifyContent};
      padding: 0 1.8rem;
    }

    .Body-native & {
      display: none;
    }
  `;
};

const SecondaryHeader: React.FunctionComponent<SecondaryHeaderProps> = (props) => (
  <div css={(theme: Theme) => computeStyles(theme, props)}>{props.children}</div>
);

export default SecondaryHeader;
